import { useEffect } from 'react';
import { fetchMortgageOffers } from 'externals/_services/product.service';
import { CLICK } from 'externals/_tracking/mixpanel/types/Mixpanel.event.types';
import { useLogger } from 'hook/error.hook';
import { useCreditInfo } from 'hook/data/creditInfo.hook';
import { useComplexState } from 'hook/helper.hook';
import { useDispatch } from 'react-redux';
import {
  FILTERS,
  SLIDER,
} from 'externals/_tracking/types/clickEventTypes.constants';
import { getRefinanceAmount } from 'helpers/credit_info';
import { wrapService } from 'actions/service_wrapper.actions';
import { unFormatCurrency } from 'helpers/money.helper';
import { useHomeLoanFieldState, optionsForLoanType } from '../helpers';
import { MORTGAGE_VERTICAL } from 'types/mrph.vertical.constants';
import { commafy } from 'helpers/number';
import { HOME_LOAN_VERTICAL } from 'types/marketplaceTiles.constants';
import { getHomeloanClickTrackingAttributes } from 'helpers/tracking.helpers';
import { buildHomeLoanQueryObject } from '../helpers/index';

const a_fetchMortgageOffers = wrapService(fetchMortgageOffers, {
  name: 'fetchMortgageOffers',
});

const sortList = [
  'Lowest APR',
  'Lowest Monthly Payment',
  'Lowest Interest Rate',
];
const sortAttr = {
  'Lowest APR': 'apr',
  'Lowest Monthly Payment': 'monthlyPayment',
  'Lowest Interest Rate': 'rate',
};

const NEW_HOME_LOAN = 'PURCHASE';
const DEFAULT_HOME_LOAN = 200000;
const MAX_HOME_LOAN = 2000000;
const SLIDER_STEP = 10000;

const useHomeLoan = (eventHandler, loanCategory, props) => {
  const logger = useLogger({
    name: 'useHomeLoan',
    categories: ['offers', `HOMELOAN`, loanCategory],
  });
  const [state, setState] = useComplexState({
    mortgageOffers: props?.ocfOffers || null,
    filters: null,
    filterTags: null,
    currentSortType: sortList[0],
    homeLoanOffersLoading: false,
    downPayment: null,
    offersError: false,
    currentLoanAmount: null,
    isOpen: false,
    loadHomeLoan: false,
  });
  const isNewHome = loanCategory === NEW_HOME_LOAN;

  // keeps this separated from the other in order
  // to take this as part of the next refactor (ticket: POD3-2298)
  const [commonStates, setCommonStates] = useComplexState({
    showModal: false,
  });

  const displayModal = () => {
    setCommonStates({
      showModal: true,
    });

    const trackingAttributes = getHomeloanClickTrackingAttributes({
      clickType: 'Search & Filter Button',
      vertical: HOME_LOAN_VERTICAL,
      module,
    });
    eventHandler(CLICK, trackingAttributes);
  };

  const hideModal = () => {
    setCommonStates({
      showModal: false,
    });

    const trackingAttributes = getHomeloanClickTrackingAttributes({
      clickType: 'Cancel Search Criteria',
      vertical: HOME_LOAN_VERTICAL,
      module,
    });
    eventHandler(CLICK, trackingAttributes);
  };

  const { mortgageOffers } = state;

  const { creditInfo } = useCreditInfo();
  const dispatch = useDispatch();

  const [
    loanAmount,
    setLoanAmountInternal,
    isLoanAmountOpen,
    setLoanAmountOpen,
  ] = useHomeLoanFieldState(null);

  const [loanType, setLoanTypeInternal, isLoanTypeOpen, setLoanTypeOpen] =
    useHomeLoanFieldState(['MORTGAGE_30Y']);
  const [
    newLoanAmount,
    setNewLoanAmountInternal,
    isNewLoanAmountOpen,
    setNewLoanAmountOpen,
  ] = useHomeLoanFieldState(DEFAULT_HOME_LOAN);
  const [
    newLoanType,
    setNewLoanTypeInternal,
    isNewLoanTypeOpen,
    setNewLoanTypeOpen,
  ] = useHomeLoanFieldState(['MORTGAGE_30Y']);

  const setLoanType = v => {
    setLoanTypeInternal(v);
  };

  const setNewLoanType = v => {
    setNewLoanTypeInternal(v);
  };

  useEffect(() => {
    if (loanAmount === null && creditInfo) {
      setLoanAmountInternal(defaultLoanAmount);
      setState({
        loadHomeLoan: true,
      });
    }
    //eslint-disable-next-line
  }, [creditInfo]);

  useEffect(() => {
    if (state.loadHomeLoan) {
      getHomeLoans();
      setState({
        loadHomeLoan: false,
      });
    }
    //eslint-disable-next-line
  }, [state.loadHomeLoan]);

  useEffect(() => {
    if (loanAmount) {
      setState({
        currentLoanAmount: isNewHome ? DEFAULT_HOME_LOAN : loanAmount,
      });
    }
    //eslint-disable-next-line
  }, [loanAmount]);

  const handleSliderUp = () => {
    eventHandler(CLICK, {
      'Click Type': FILTERS,
      'Filter Type': 'Mortgage Loan Amount',
      Vertical: MORTGAGE_VERTICAL,
    });

    const maxRangeLoanDifference = MAX_HOME_LOAN - state.currentLoanAmount;
    if (maxRangeLoanDifference < SLIDER_STEP) {
      setState({
        currentLoanAmount: MAX_HOME_LOAN,
      });
    }
  };

  const onResetFilter = () => {
    setState({
      currentLoanAmount: isNewHome ? DEFAULT_HOME_LOAN : defaultLoanAmount,
    });
    setNewLoanTypeInternal(['MORTGAGE_30Y']);
    setCurrentSortType(sortList[0]);

    const trackingAttributes = getHomeloanClickTrackingAttributes({
      clickType: 'Reset Search Criteria',
      vertical: HOME_LOAN_VERTICAL,
      module,
    });
    eventHandler(CLICK, trackingAttributes);
  };

  const getHomeLoans = query => {
    setState({
      homeLoanOffersLoading: true,
    });
    let mortgageQueryOptions = query;
    const defaultLoadNewHome = isNewHome ? DEFAULT_HOME_LOAN : loanAmount;
    if (!mortgageQueryOptions) {
      mortgageQueryOptions = {
        userId: 'me',
        loanType: loanCategory,
        loanAmount: state.currentLoanAmount || defaultLoadNewHome,
        mortgageProductCode: newLoanType,
        rateSource: 'EXTERNAL',
        itemsPerPage: 20,
      };
    }

    const currentFilters = {
      loanType: newLoanType,
      loanAmount: state.currentLoanAmount || defaultLoadNewHome,
    };

    setState({ filters: currentFilters, mortgageOffers: null });

    dispatch(a_fetchMortgageOffers(mortgageQueryOptions))
      .then(res => {
        const sortedItems = res.items.sort((a, b) => {
          return (
            a[sortAttr[state.currentSortType]] -
            b[sortAttr[state.currentSortType]]
          );
        });

        setState({
          mortgageOffers: { ...res, items: sortedItems },
        });
      })
      .catch(err => {
        logger.reportAPIError(err);
        logger.errorException(err);
        setState({
          offersError: true,
        });
      })
      .finally(() => {
        setState({
          homeLoanOffersLoading: false,
        });
      });
  };

  const onApplyFilter = () => {
    const tags = {};

    if (state.currentSortType !== sortList[0]) {
      tags.sortBy = {
        value: state.currentSortType,
        label: `Sort: ${state.currentSortType}`,
      };
    }

    if (state.currentLoanAmount !== loanAmount) {
      tags.loanAmount = {
        value: state.currentLoanAmount,
        label: `Loan $${commafy(state.currentLoanAmount)}`,
      };
    }

    const tempNewLoanType =
      newLoanType.length === 1 ? newLoanType[0] : newLoanType;
    const tempLoanType = loanType.length === 1 ? loanType[0] : loanType;

    if (tempNewLoanType !== tempLoanType) {
      tags.loanType = {
        value: tempNewLoanType,
        label: `Loan Type: ${
          optionsForLoanType.find(opt => opt.value === tempNewLoanType)?.label
        }`,
      };
    }

    setState({
      filterTags: tags,
    });
    if (!state.homeLoanOffersLoading && (loanAmount || newLoanAmount)) {
      getHomeLoans();
    }
    setCommonStates({
      showModal: false,
    });
  };

  const onRemoveTagFilter = key => {
    const defaultValue = {
      sortBy: sortList[0],
      loanAmount: isNewHome ? DEFAULT_HOME_LOAN : defaultLoanAmount,
      loanType: ['MORTGAGE_30Y'],
    };

    let newFilterValues = {
      sortBy: state.currentSortType,
      loanAmount: state.currentLoanAmount,
      loanType: newLoanType,
    };

    let updatedKey = { [key]: { value: defaultValue[key], label: null } };

    switch (key) {
      case 'sortBy':
        setState({
          filterTags: {
            ...state.filterTags,
            ...updatedKey,
          },
          currentSortType: sortList[0],
        });
        newFilterValues.sortBy = sortList[0];
        break;
      case 'loanAmount':
        updatedKey = {
          ...updatedKey,
          currentLoanAmount: { value: defaultValue.loanAmount, label: null },
        };
        setState({
          filterTags: {
            ...state.filterTags,
            ...updatedKey,
          },
          currentLoanAmount: isNewHome ? DEFAULT_HOME_LOAN : defaultLoanAmount,
        });
        newFilterValues.loanAmount = isNewHome
          ? DEFAULT_HOME_LOAN
          : defaultLoanAmount;
        break;
      case 'loanType':
        setState({
          filterTags: {
            ...state.filterTags,
            ...updatedKey,
          },
        });
        setNewLoanTypeInternal(['MORTGAGE_30Y']);
        newFilterValues.loanType = ['MORTGAGE_30Y'];
        break;
      default:
        break;
    }
    getHomeLoans(
      buildHomeLoanQueryObject(
        {
          loanAmount: newFilterValues.loanAmount,
          loanType: newFilterValues.loanType,
        },
        loanCategory,
      ),
    );
  };

  const addFilterTracking = (f, filterName, isSlider) => {
    return val => {
      if (f) {
        f(val);
      }
      eventHandler(CLICK, {
        'Click Type': FILTERS,
        'Filter Type': filterName,
        Vertical: MORTGAGE_VERTICAL,
      });

      if (isSlider) {
        eventHandler(CLICK, {
          'Click Type': SLIDER,
          'Slider Name': filterName,
          Vertical: MORTGAGE_VERTICAL,
        });
      }
    };
  };

  const onDownPaymentUpdate = e => {
    let val = unFormatCurrency(e.target.value);
    if (val === '' || /^[0-9]+$/i.test(val)) {
      setState({
        downPayment: val,
      });
    }
  };

  const defaultLoanAmount = getRefinanceAmount(creditInfo, DEFAULT_HOME_LOAN);

  const setCurrentSortType = e => {
    setState({
      currentSortType: e,
    });

    eventHandler(CLICK, {
      'Click Type': e,
      Vertical: HOME_LOAN_VERTICAL,
    });
  };

  const setIsOpen = value => {
    setState({
      isOpen: value,
    });
  };

  const setCurrentLoanAmount = value => {
    setState({
      currentLoanAmount: value,
    });
  };

  const getDefaultHomeLoanMinimum = () => {
    if (
      !isNewHome &&
      defaultLoanAmount < DEFAULT_HOME_LOAN &&
      !props?.isOcfPage
    ) {
      return defaultLoanAmount;
    }
    return DEFAULT_HOME_LOAN;
  };

  return {
    ...state,
    setState,
    mortgageOffers,
    onDownPaymentUpdate,
    optionsForLoanType,
    loanAmount,
    setLoanAmount: addFilterTracking(
      setLoanAmountInternal,
      'Mortgage Loan Amount',
      true,
    ),
    setCurrentLoanAmount,
    isLoanAmountOpen,
    setLoanAmountOpen,
    loanType,
    setLoanType: addFilterTracking(setLoanType, 'Mortgage Loan Type'),
    isLoanTypeOpen,
    setLoanTypeOpen,
    newLoanAmount,
    setNewLoanAmount: addFilterTracking(
      setNewLoanAmountInternal,
      'Mortgage Loan Amount',
      true,
    ),
    isNewLoanAmountOpen,
    setNewLoanAmountOpen,
    newLoanType,
    setNewLoanType: addFilterTracking(setNewLoanType, 'Mortgage Loan Type'),
    isNewLoanTypeOpen,
    setNewLoanTypeOpen,
    setIsOpen,
    homeLoanMinimum: getDefaultHomeLoanMinimum(),
    homeLoanMaximum: MAX_HOME_LOAN,
    defaultLoanAmount,
    handleSliderUp,
    onResetFilter,
    filterTags: state.filterTags,
    sortList,
    currentSortType: state.currentSortType,
    setCurrentSortType,
    onApplyFilter,
    onRemoveTagFilter,
    commonStates,
    displayModal,
    hideModal,
    SLIDER_STEP,
  };
};

export default useHomeLoan;
